import { ReactNode, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

/**
 * Wraps wide content in a horizontally scrollable area without ever showing a vertical
 * scrollbar (the page handles vertical scroll). Because the content keeps its natural
 * height, the native horizontal scrollbar would sit at the very bottom of a tall table
 * and be unreachable — so we hide it and render a thin scrollbar pinned to the bottom of
 * the viewport (`position: sticky`) that stays in view and stays in sync with the content.
 *
 * The `className` is applied to the outer frame so callers can add a border / spacing.
 */
export const StickyHorizontalScroll = ({
    children,
    className,
}: {
    children: ReactNode;
    className?: string;
}): JSX.Element => {
    const contentRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);
    const scrollbarRef = useRef<HTMLDivElement>(null);
    const [scrollWidth, setScrollWidth] = useState(0);
    const [hasOverflow, setHasOverflow] = useState(false);

    useEffect(() => {
        const content = contentRef.current;
        const inner = innerRef.current;
        if (!content || !inner) {
            return;
        }
        const recompute = (): void => {
            setScrollWidth(content.scrollWidth);
            setHasOverflow(content.scrollWidth - content.clientWidth > 1);
        };
        recompute();
        const resizeObserver = new ResizeObserver(recompute);
        resizeObserver.observe(content);
        resizeObserver.observe(inner);
        return () => resizeObserver.disconnect();
    }, []);

    // Both handlers only assign when the values differ, so the synced scroll converges
    // instead of looping (assigning the current scrollLeft does not fire a scroll event).
    const syncScrollbarToContent = (): void => {
        const content = contentRef.current;
        const scrollbar = scrollbarRef.current;
        if (content && scrollbar && scrollbar.scrollLeft !== content.scrollLeft) {
            scrollbar.scrollLeft = content.scrollLeft;
        }
    };

    const syncContentToScrollbar = (): void => {
        const content = contentRef.current;
        const scrollbar = scrollbarRef.current;
        if (content && scrollbar && content.scrollLeft !== scrollbar.scrollLeft) {
            content.scrollLeft = scrollbar.scrollLeft;
        }
    };

    return (
        <Frame className={className}>
            <Content ref={contentRef} onScroll={syncScrollbarToContent}>
                <Inner ref={innerRef}>{children}</Inner>
            </Content>
            {hasOverflow ? (
                <StickyScrollbar ref={scrollbarRef} onScroll={syncContentToScrollbar}>
                    <ScrollbarSpacer style={{ width: `${scrollWidth}px` }} />
                </StickyScrollbar>
            ) : null}
        </Frame>
    );
};

const Frame = styled.div`
    min-width: 0;
`;

const Content = styled.div`
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }

    @media print {
        overflow: visible;
    }
`;

const Inner = styled.div`
    width: max-content;
    min-width: 100%;

    @media print {
        width: auto;
    }
`;

const StickyScrollbar = styled.div`
    position: sticky;
    bottom: 0;
    z-index: 1;
    overflow-x: auto;
    overflow-y: hidden;

    &::-webkit-scrollbar {
        height: 12px;
        -webkit-appearance: none;
    }

    &::-webkit-scrollbar-thumb {
        background-color: ${({ theme }) => theme.darkBorderColor};
        border-radius: 6px;
        border: 3px solid ${({ theme }) => theme.cardBackgroundColor};
    }

    @media print {
        display: none;
    }
`;

const ScrollbarSpacer = styled.div`
    height: 1px;
`;
